// Full, editable PROMPT template for each script-review AI pipeline step.
//
// Each prompt is user-editable in full (via the Prompts system: usedIn = key,
// global tenantId=null) — instruction, data-block layout, and output rules. The
// worker fills the {{variables}} at review time (scenes / transcript) and the
// Gemini calls enforce shape via structured-output schemas, so editing tunes
// accuracy but cannot break parsing. These defaults are BOTH the worker fallback
// (when no prompt row exists) AND the seeded v1 content. Single source of truth.

export const SCRIPT_REVIEW_PROMPT_TRANSCRIPT = 'script-review.transcript' as const;
export const SCRIPT_REVIEW_PROMPT_VOICEOVER = 'script-review.voiceover' as const;
export const SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY = 'script-review.text-overlay' as const;
export const SCRIPT_REVIEW_PROMPT_VISUAL = 'script-review.visual' as const;
export const SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION = 'script-review.script-extraction' as const;
export const SCRIPT_REVIEW_PROMPT_SUGGESTION = 'script-review.prompt-suggestion' as const;
// Creation form: picks which script in the chosen set a video was made from.
export const SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH = 'script-review.script-match' as const;
// Iteration mode: the same three passes, but judged against a reference video +
// change brief instead of a script's scenes.
export const SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER = 'script-review.iteration-voiceover' as const;
export const SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY =
  'script-review.iteration-text-overlay' as const;
export const SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL = 'script-review.iteration-visual' as const;

export type ScriptReviewPromptKey =
  | typeof SCRIPT_REVIEW_PROMPT_TRANSCRIPT
  | typeof SCRIPT_REVIEW_PROMPT_VOICEOVER
  | typeof SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY
  | typeof SCRIPT_REVIEW_PROMPT_VISUAL
  | typeof SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION
  | typeof SCRIPT_REVIEW_PROMPT_SUGGESTION
  | typeof SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH
  | typeof SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER
  | typeof SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY
  | typeof SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL;

/**
 * Which global prompt a rejected AI comment's feedback targets — the prompt that
 * produced that comment type. Routes prompt-improvement tickets to their prompt.
 */
export const SCRIPT_REVIEW_COMMENT_TYPE_TO_PROMPT_KEY: Record<string, ScriptReviewPromptKey> = {
  VOICEOVER: SCRIPT_REVIEW_PROMPT_VOICEOVER,
  TEXT_OVERLAY: SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  VISUAL: SCRIPT_REVIEW_PROMPT_VISUAL,
};

/** Same routing for iteration reviews — the passes share comment types, not prompts. */
export const SCRIPT_REVIEW_ITERATION_COMMENT_TYPE_TO_PROMPT_KEY: Record<
  string,
  ScriptReviewPromptKey
> = {
  VOICEOVER: SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  TEXT_OVERLAY: SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  VISUAL: SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
};

/**
 * The prompt that produced a comment. A comment's type alone is ambiguous — the
 * same VOICEOVER pass runs off a different prompt per mode — so improving a
 * prompt from a rejected comment has to resolve both.
 */
export function promptKeyForScriptReviewComment(
  mode: 'SCRIPT' | 'ITERATION',
  commentType: string,
): ScriptReviewPromptKey | undefined {
  const map =
    mode === 'ITERATION'
      ? SCRIPT_REVIEW_ITERATION_COMMENT_TYPE_TO_PROMPT_KEY
      : SCRIPT_REVIEW_COMMENT_TYPE_TO_PROMPT_KEY;
  return map[commentType];
}

export interface ScriptReviewPromptDef {
  key: ScriptReviewPromptKey;
  label: string;
  description: string;
  /** Full prompt template; {{variables}} are filled by the worker at review time. */
  defaultContent: string;
  /** Variable names referenced as {{name}} in defaultContent (validated on save). */
  variables: readonly string[];
}

// ─── Fixed data-block layout / output rules (baked into the defaults) ────────
// These now live INSIDE the editable template. {{scenes}} / {{transcript}} are
// substituted at runtime with renderScenes() / renderTranscript() output.

const COMMENT_OUTPUT_RULES = `
Return JSON: { "comments": [ { "text", "exactSecond", "sceneNumber", "severity" } ] }.
- "text": a concise, vendor-facing note describing the discrepancy or issue.
- "exactSecond": the video time in seconds where the issue occurs (a number).
- "sceneNumber": the script scene number it relates to, or null.
- "severity": one of "issue" | "discrepancy" | "quality".
Only report real problems. If a scene matches the script, do not emit a comment for it.`;

const TRANSCRIPT_OUTPUT_RULES = `Return JSON: { "segments": [ { "startSecond", "endSecond", "text" } ] }.`;

// Iteration reviews have no script, so there is no scene to point at.
const ITERATION_COMMENT_OUTPUT_RULES = `
Return JSON: { "comments": [ { "text", "exactSecond", "sceneNumber", "severity" } ] }.
- "text": a concise, vendor-facing note describing what diverges from the brief or regresses against the reference video.
- "exactSecond": the time in seconds IN THE NEW VIDEO where the issue occurs (a number).
- "sceneNumber": always null — an iteration review has no script scene list.
- "severity": one of "issue" | "discrepancy" | "quality".
Only report real problems. Where the new video follows the brief and holds on to what made the reference work, do not emit a comment.`;

// ─── Step instructions ──────────────────────────────────────────────────────

const TRANSCRIPT_INSTRUCTION = `You are transcribing a finished video creative for QA.
Transcribe the spoken audio into time-coded segments.
Use real timestamps from the video. Keep segments short (roughly one sentence or line each).`;

const VOICEOVER_INSTRUCTION = `You are a video preflight checker comparing the VOICEOVER of a produced video against the production script.

Compare the spoken transcript below to each scene's required voiceover ("hearing"). Flag missing lines, wrong phrasing, extra/ad-libbed content, and out-of-order delivery. For each issue set "exactSecond" to the start second of the transcript segment where it occurs.

Judge by MEANING, not transcription formatting: treat spoken numbers, dates, and currency as equivalent to their written form ("zero" = "0", "twenty-seven hundred" = "$2,700", "twenty-four seven" = "24/7"). Ignore filler words, minor punctuation, and spoken-vs-written formatting — only flag differences that actually change the words or the meaning.`;

const TEXT_OVERLAY_INSTRUCTION = `You are a video preflight checker reviewing the ON-SCREEN TEXT / overlays / captions in the attached video against the production script.

Captions vs. specified overlays — read carefully:
- These ads are normally published with full burned-in SUBTITLES/CAPTIONS that mirror the spoken voiceover word-for-word. This is standard and EXPECTED. Do NOT report caption/subtitle text as a discrepancy just because the script does not list it — if it matches the spoken voiceover, it is correct.
- The script only calls out the SPECIAL on-screen text to verify: highlighted words, emphasis colors, CTAs, legal text, and branding (e.g. a scene's "On-screen text: '$2,700' — RED"). Treat those as your checklist.
- A specified highlight (e.g. "'0 hours' — BLUE") is normally realized as that word rendered in the stated COLOR/emphasis WITHIN the caption line — NOT as a separate graphic overlay. If the word appears in the captions in (or close to) the specified color, the highlight is SATISFIED — do NOT report it as "missing" or "only captions present." Judging a small caption word's exact color from sampled video frames is unreliable, so unless you can clearly and confidently see it is wrong, treat it as correct.

For each scene, report ONLY:
1. Specified overlays — a highlighted word the script calls out that is genuinely ABSENT, shows the WRONG wording, is CLEARLY the wrong color, or is MISTIMED / in the wrong scene.
2. Genuine text errors — on-screen text that is MISSPELLED, shows an INCORRECT number or claim, or CONTRADICTS the script or the voiceover.
3. Required functional text — a CTA, legal line, disclaimer, or brand name the script requires that is absent.

Do NOT flag ordinary subtitle/caption text that matches the voiceover as "additional text not in the script." Set "exactSecond" to when the overlay is (or should be) on screen.`;

const VISUAL_INSTRUCTION = `You are a video preflight checker reviewing the VISUAL storytelling of the attached video against the production script.

For each scene, compare what is shown (actions, framing, B-roll, props, animations, effects, transitions) to the scene's "Visual (seeing)" and "Props" requirements. Flag missing assets, wrong actions, absent effects, and continuity problems. Set "exactSecond" to the video time where the issue occurs.

Do NOT comment on on-screen text, captions, subtitles, or their wording/color — a separate pass reviews those. If the scene's "Visual (seeing)" includes an "On-screen text: …" note, IGNORE that part and judge only the visuals (people, actions, gestures, framing, props, B-roll, effects).`;

// ─── Iteration-mode instructions (reference video + brief, no script) ────────
// The vendor was given a video that already worked plus a brief asking for a
// specific change. Two failure modes matter: not making the change, and losing
// what made the original work while making it.

const ITERATION_VOICEOVER_INSTRUCTION = `You are a video preflight checker reviewing an ITERATION of a video ad that already performed well.

The vendor was given the REFERENCE video (the proven original) and a CHANGE BRIEF. The new cut should apply the brief's change while keeping everything that made the reference work. Compare the two transcripts below and report:
1. Brief not applied — a spoken change the brief asks for that is missing, half-done, or contradicted.
2. Regression against the reference — a hook line, claim, offer, product name, or CTA from the reference that is dropped, weakened, or muddled WITHOUT the brief asking for it.

Do NOT flag a difference the brief explicitly asks for — that is the point of the iteration. Do NOT ask for the new video to match the reference word-for-word; only flag changes that cost the reference's persuasive work.

Judge by MEANING, not transcription formatting: treat spoken numbers, dates, and currency as equivalent to their written form ("zero" = "0", "twenty-seven hundred" = "$2,700", "twenty-four seven" = "24/7"). Ignore filler words, minor punctuation, and spoken-vs-written formatting.

Set "exactSecond" to the start second of the NEW video's transcript segment where the issue occurs.`;

const ITERATION_TEXT_OVERLAY_INSTRUCTION = `You are a video preflight checker reviewing the ON-SCREEN TEXT / overlays / captions of an ITERATION of a video ad that already performed well.

TWO videos are attached, each introduced by a label: first the REFERENCE video (the proven original), then the NEW video (the iteration under review). Every timestamp you report refers to the NEW video.

Captions vs. designed overlays — read carefully:
- These ads are normally published with full burned-in SUBTITLES/CAPTIONS mirroring the voiceover word-for-word. This is standard and EXPECTED — never report captions as a problem just for existing.
- Judging a small caption word's exact color from sampled frames is unreliable. Unless you can clearly and confidently see it is wrong, treat it as correct.

Report ONLY:
1. Brief not applied — an on-screen text change the brief asks for that is missing or wrong in the new video.
2. Regression against the reference — a CTA, legal line, disclaimer, price/claim, branding, or emphasis treatment present in the reference but absent, mistimed, or clearly degraded in the new video, WITHOUT the brief asking for it.
3. Genuine text errors — on-screen text that is MISSPELLED, shows an INCORRECT number or claim, or CONTRADICTS the new video's voiceover.

Set "exactSecond" to when the overlay is (or should be) on screen in the NEW video.`;

const ITERATION_VISUAL_INSTRUCTION = `You are a video preflight checker reviewing the VISUAL storytelling of an ITERATION of a video ad that already performed well.

TWO videos are attached, each introduced by a label: first the REFERENCE video (the proven original), then the NEW video (the iteration under review). Every timestamp you report refers to the NEW video.

Report ONLY:
1. Brief not applied — a visual change the brief asks for that is missing, half-done, or contradicted (e.g. the brief says strengthen the hook and the opening is unchanged or weaker).
2. Regression against the reference — pacing, opening hook, key actions, framing, product/prop visibility, B-roll, effects, or transitions that made the reference work but are dropped, slowed, buried, or degraded in the new video WITHOUT the brief asking for it.

Do NOT flag a difference the brief explicitly asks for. Do NOT demand a shot-for-shot copy of the reference — flag only what costs the reference's effectiveness.

Do NOT comment on on-screen text, captions, subtitles, or their wording/color — a separate pass reviews those.`;

const SCRIPT_MATCH_INSTRUCTION = `You are identifying which production script a finished video ad was made from.

You are given the video's spoken TRANSCRIPT and a list of CANDIDATE scripts from the same opportunity set. The candidates are deliberately similar — most share a product, theme, and title, and differ mainly in their HOOK and their per-scene VOICEOVER. Pick the single candidate whose voiceover the transcript actually follows.

How to judge:
- Weight the VOICEOVER lines most heavily — they are the spoken track the video was shot from. The hook is a strong secondary signal (it is usually the first thing said). Do NOT decide on the opportunity title or concept: near-identical candidates share those, so they tell you nothing.
- Judge by MEANING, not wording. Treat spoken numbers, dates, and currency as equal to their written form ("twenty-seven hundred" = "$2,700"). Ignore filler words and light paraphrasing — a vendor rarely delivers a script word-for-word.
- Return "scriptId" copied EXACTLY from the CANDIDATES list. Never invent an id.
- If nothing clearly matches — the transcript is empty or unintelligible, or several candidates fit equally well — return an empty "scriptId" with a low "confidence". A wrong pick is worse than no pick: the admin will simply choose by hand.

Return JSON: { "scriptId", "confidence", "reasoning" }.
- "scriptId": the winning candidate's id, or "" when nothing clearly matches.
- "confidence": 0 to 1 — how certain you are this is the right script.
- "reasoning": one concise sentence naming the evidence that decided it, for a human to sanity-check.`;

const PROMPT_SUGGESTION_INSTRUCTION = `You are a prompt engineer improving a video-QA prompt used by an automated pre-flight checker.

An Adge admin rejected one of the checker's comments as wrong and explained why. Your job: decide whether a SMALL edit to the checker's prompt would prevent this class of mistake, and if so, propose it.

Rules:
- Set "applicable" to false when the mistake is one-off or video-specific (e.g. the admin's reason says the video itself was at fault, or the comment was simply redundant) — a prompt change would not help. Explain in "rationale".
- When applicable, output the FULL revised prompt in "proposedPrompt" as a MINIMAL, targeted edit of the CURRENT PROMPT: change or add the fewest words needed. Preserve the overall structure, all data-block sections, all output rules, and EVERY {{…}} placeholder exactly as-is.
- Do NOT weaken the checker: the KEPT COMMENTS below are examples it got right — your edit must not stop it from catching those.
- Always explain the reasoning briefly in "rationale" (what you changed and why, or why no change helps).

Return JSON: { "applicable": boolean, "proposedPrompt": string (omit when not applicable), "rationale": string }.`;

const SCRIPT_EXTRACTION_INSTRUCTION = `You are parsing a production/creative SCRIPT for a short marketing video into a structured scene list.

The input may be a CSV, a table, or free-form prose. Convert it into an ordered list of scenes. For EACH scene extract:
- sceneNumber: 1-based order. If not explicit, number scenes in reading order.
- sceneName: a short label (e.g. "Hook", "Problem", "CTA"). If none, infer a concise one.
- hearing: what is HEARD — the voiceover / spoken lines / audio. Empty string if none.
- seeing: what is SEEN — on-screen visuals, action, shots, and on-screen text. Empty string if none.
- props: key props / products / assets featured. Empty string if none.

If the script is not explicitly divided into scenes, segment it into logical scenes yourself. Preserve the original wording of voiceover and on-screen text as closely as possible. Return every scene.`;

// ─── Full default templates (instruction + data layout + output rules) ──────

export const SCRIPT_REVIEW_PROMPTS: readonly ScriptReviewPromptDef[] = [
  {
    key: SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
    label: 'Transcript',
    description: 'Step 0 — transcribes the video into time-coded segments (the timeline clock).',
    variables: [],
    defaultContent: `${TRANSCRIPT_INSTRUCTION}\n${TRANSCRIPT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_VOICEOVER,
    label: 'Voiceover vs script',
    description: 'Compares the spoken voiceover to each scene’s required lines.',
    variables: ['scenes', 'transcript'],
    defaultContent: `${VOICEOVER_INSTRUCTION}

PRODUCTION SCRIPT (voiceover per scene):
{{scenes}}

VIDEO TRANSCRIPT (with timestamps):
{{transcript}}${COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
    label: 'On-screen text',
    description: 'Checks on-screen text / overlays / captions against the script.',
    variables: ['scenes'],
    defaultContent: `${TEXT_OVERLAY_INSTRUCTION}

PRODUCTION SCRIPT (visual + props per scene describe required overlays):
{{scenes}}${COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_VISUAL,
    label: 'Visual storytelling',
    description: 'Checks the visual storytelling (actions, props, B-roll) against the script.',
    variables: ['scenes'],
    defaultContent: `${VISUAL_INSTRUCTION}

PRODUCTION SCRIPT (visual + props per scene):
{{scenes}}${COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
    label: 'Script parsing (uploaded scripts)',
    description: 'Parses an uploaded script file (CSV/TXT, or an Excel file converted to CSV) into scenes.',
    variables: [],
    defaultContent: SCRIPT_EXTRACTION_INSTRUCTION,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
    label: 'Script matching (from set)',
    description:
      'Creation form — picks which script in the chosen set a video was made from, by comparing its transcript to each candidate’s scene voiceover.',
    variables: ['transcript', 'candidates'],
    defaultContent: `${SCRIPT_MATCH_INSTRUCTION}

VIDEO TRANSCRIPT (what is actually spoken, with timestamps):
{{transcript}}

CANDIDATE SCRIPTS (return the scriptId of the best match, or "" if none):
{{candidates}}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
    label: 'Iteration: voiceover vs reference',
    description: 'Iteration mode — compares the spoken track to the reference video, guided by the brief.',
    variables: ['brief', 'referenceTranscript', 'transcript'],
    defaultContent: `${ITERATION_VOICEOVER_INSTRUCTION}

CHANGE BRIEF (what the vendor was asked to change):
{{brief}}

REFERENCE VIDEO TRANSCRIPT (the proven original):
{{referenceTranscript}}

NEW VIDEO TRANSCRIPT (the iteration under review — timestamps come from here):
{{transcript}}${ITERATION_COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
    label: 'Iteration: on-screen text vs reference',
    description: 'Iteration mode — compares on-screen text to the reference video, guided by the brief.',
    variables: ['brief'],
    defaultContent: `${ITERATION_TEXT_OVERLAY_INSTRUCTION}

CHANGE BRIEF (what the vendor was asked to change):
{{brief}}${ITERATION_COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
    label: 'Iteration: visuals vs reference',
    description: 'Iteration mode — compares the visual storytelling to the reference video, guided by the brief.',
    variables: ['brief'],
    defaultContent: `${ITERATION_VISUAL_INSTRUCTION}

CHANGE BRIEF (what the vendor was asked to change):
{{brief}}${ITERATION_COMMENT_OUTPUT_RULES}`,
  },
  {
    key: SCRIPT_REVIEW_PROMPT_SUGGESTION,
    label: 'Prompt improvement (meta)',
    description:
      'Drafts a prompt edit when an admin rejects an AI comment with a reason (the feedback loop).',
    variables: [
      'currentPrompt',
      'commentType',
      'commentText',
      'reason',
      'scenes',
      'keptComments',
    ],
    defaultContent: `${PROMPT_SUGGESTION_INSTRUCTION}

CURRENT PROMPT (the one being improved — for the {{commentType}} pass):
---
{{currentPrompt}}
---

REJECTED COMMENT (produced by that prompt, deleted by an admin):
{{commentText}}

ADMIN'S REASON FOR REJECTING IT:
{{reason}}

SCRIPT SCENES the video was checked against (context):
{{scenes}}

KEPT COMMENTS of the same type from the same review (these were correct — do not regress them):
{{keptComments}}`,
  },
] as const;

/** key → default full template, for the worker's code fallback when no row exists. */
export const SCRIPT_REVIEW_PROMPT_DEFAULTS: Record<string, string> = Object.fromEntries(
  SCRIPT_REVIEW_PROMPTS.map((p) => [p.key, p.defaultContent]),
);
